import moment from 'moment';

export const LogLevel = {
	'INFO': 0,
	'DEBUG': 1,
	'WARNING': 2
};

export const KeyCodes = {
	'KEY_TAB': 9,
	'KEY_ENTER': 13,
	'KEY_SHIFT': 16,
	'KEY_CTRL': 17,
	'KEY_ESC': 27,
	'SPACE_BAR': 32,
	'KEY_LEFT': 37,
	'KEY_UP': 38,
	'KEY_RIGHT': 39,
	'KEY_DOWN': 40
};

export const HttpReasonCodes = {
	'PARTIAL_CONTENT': 206,
	'OK': 200,
	'MULTI_STATUS': 207
};

export const GADGET_DISPLAY_MODES = {
	CANVAS: 'canvas',
	DEFAULT: 'default'
};

export default class ComponentsUtil {
/** method to set focus on an element based on the class name
 * @param {string} className
 * 			 name of the css class
 */
	static setFocusByClassname (className) {
		this.log('ComponentsUtil', LogLevel.DEBUG,
			`Setting focus on element having classname ${className}`);
		document.getElementsByClassName(className)[0] && document.getElementsByClassName(className)[0].focus();
	}

	static setFocusByElementID (id) {
		this.log('ComponentsUtil', LogLevel.DEBUG,
			`Setting focus on element having id ${id}`);
		document.getElementById(id) && document.getElementById(id).focus();
	}

	static cursorPosition (id) {
		const element = document.getElementById(id);
		const value = element.value;
		if(value == ""){
			if (element) {
				let curPos = 0;
				if (element.setSelectionRange !== 'undefined') {
					element.setSelectionRange(curPos, curPos);
				}
			}
		}		
	}

	static clickByElementID (id) {
		this.log('ComponentsUtil', LogLevel.DEBUG,
			`Setting focus on element having id ${id}`);
		document.getElementById(id) && document.getElementById(id).click();
	}

	static subscribeToHubTopic (topic, callback) {
		window.finesse.containerservices.ContainerServices.addHandler(topic, callback);
	}

	static unsubscribeToHubTopic (topic, callback) {
		window.finesse.containerservices.ContainerServices.removeHandler(topic, callback);
	}

	static log (component, logLevel, message) {
		window.finesse.clientservices.ClientServices.log(`[ ${component} ] - ${message}`);
	}

	static addClassById (id, className) {
		this.log('ComponentsUtil', LogLevel.DEBUG,
			`Adding class ${className} on element having id ${id}`);
		document.getElementById(id).classList.add(className);
	}

	static removeClassById (id, className) {
		this.log('ComponentsUtil', LogLevel.DEBUG,
			`Removing class ${className} on element having id ${id}`);
		document.getElementById(id).classList.remove(className);
	}

	static encodeSpecialChars (str = '') {
		// Encode special characters
		return str.replace(/&/g, '&amp;').replace(/</g, '&lt;')
			.replace(/>/g, '&gt;').replace(/"/g, '&quot;')
			.replace(/'/g, '&#x27;').replace(/\//g, '&#x2f;');
	}

	static getVoiceChannelLabel = (selectedState) => {
		if (selectedState.label !== 'Not Ready' && selectedState.value.startsWith('NOT_READY_')) {
			return 'voice.channel.NOT_READY_WITH_REASON_CODE';
		} else {
			return 'voice.channel.label';
		}
	};

	static getByteLen = (callavariableValue) => {
		let byteLen = 0;
		for (let i = 0; i < callavariableValue.length; i++) {
			byteLen += ComponentsUtil.getLengthOfChar(callavariableValue.charCodeAt(i));
		}
		return byteLen;
	}

	static getLengthOfChar = (c) =>
		(c < (1 << 7) ? 1
			: c < (1 << 11) ? 2
				: c < (1 << 16) ? 3
					: c < (1 << 21) ? 4
						: c < (1 << 26) ? 5
							: c < (1 << 31) ? 6 : Number.NaN);

	/**
	 * @param
	 * url
	 * fromLocaleStr - Current locale
	 * toLocaleStr - Locale to be changed to
	 */
	static replaceLocale = (url, fromLocaleStr, toLocaleStr) => {
		var result, indexOfLocale, oldLocaleStr, newLocaleStr, startStr, endStr, indexOfQuestion;

		oldLocaleStr = 'locale=' + fromLocaleStr;
		newLocaleStr = 'locale=' + toLocaleStr;
		indexOfQuestion = url.indexOf('?');
		indexOfLocale = url.indexOf(oldLocaleStr);
		if (indexOfLocale === -1) {
			if (indexOfQuestion === -1) {
				result = url + '?' + newLocaleStr;
			} else if (indexOfQuestion === url.length - 1) {
				result = url + newLocaleStr;
			} else {
				result = url + '&' + newLocaleStr;
			}
		} else {
			startStr = url.substring(0, indexOfLocale);
			endStr = url.substring(indexOfLocale + oldLocaleStr.length);

			result = startStr + newLocaleStr + endStr;
		}

		return result;
	}
}

/**
 * Deep copy json object
 * @param input
 * @returns {*}
 */
export const deepCopy = function (input) {
	let newObj;
	if (!input || typeof input !== 'object') {
		return input;
	}

	if (Array.isArray(input)) {
		return input.map(arrEle => deepCopy(arrEle));
	}

	newObj = {};
	Object.keys(input).forEach(key => {
		newObj[key] = deepCopy(input[key]);
	});

	return newObj;
};
/**
 * Removes spaces and replaces with - and converts string to lowercase
 * @param {text} text to clean
 * @return {String}
 * */
export const replaceSpecialChars = (text = '', withChar = '') =>
	String(text).toLowerCase().replace(/[^a-z0-9]/g, withChar);

/**
 * Generates unique id (timestamp) and appends salt
 *
 * @param {salt} optional
 * @return {String}
 * */
export const generateUniqueId = (salt = '') => replaceSpecialChars(salt) + Date.now();

/**
 * Only digits and some special characters(+, *, #, :) are allowed
 *
 * @param {String} number
 * @return {String}
 * */
export const sanitizeNumber = number => window.gadgets.Prefs().getString("deploymentType") === 'UCCX' ? number.replace(/[^\d+*#:]/g, '') : number.replace(/[^\w+*#:]|_/g, '');

/**
 * Generates time with a given timestamp in AM/PM format
 *
 * @param {timestamp}
 * @return {String}
 * */
export const getFormattedTime = (timestamp) => new Date(timestamp).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });

export const getDateFormat = (locale = 'en-US') => {
	moment.locale(locale);
	return moment.localeData().longDateFormat('L');
};

export const formatBytes = (bytes, decimals) => {
	if (bytes === 0) return '0 Bytes';
	var k = 1024,
		dm = decimals || 2,
		sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
		i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getIntValue = (strVal, defaultVal) => {
	try {
		return parseInt(strVal);
	} catch (e) {
		return defaultVal;
	}
};

/**
 * Strip HTML from text
 * In our messages properties, there are text like
 * You will be automatically signed out in about <b>{0} minutes</b> due to session expiry.
 * For accessiblity , the html tags are removed
 */

export const getStrippedHTMLtext = (message) => {
	if (!message) return '';
	let tmp = document.createElement('div');
	tmp.innerHTML = message;
	const text = tmp.textContent;
	/* clonedNode.remove is not supported in IE */
	/* istanbul ignore next */
	if (tmp.remove) {
		tmp.remove();
	} else {
		if (tmp.parentNode) {
			tmp.parentNode.removeChild(tmp);
		}
	}
	return text;
};

export const removeParam = (key, sourceURL) => {
	var rtn = sourceURL.split('?')[0],
		param,
		paramsArr = [],
		queryString = (sourceURL.indexOf('?') !== -1) ? sourceURL.split('?')[1] : '';
	if (queryString !== '') {
		paramsArr = queryString.split('&');
		for (var i = paramsArr.length - 1; i >= 0; i -= 1) {
			param = paramsArr[i].split('=')[0];
			if (param === key) {
				paramsArr.splice(i, 1);
			}
		}
		rtn = rtn + '?' + paramsArr.join('&');
	}
	return rtn;
};

/**
 * Return Hour:Minutes AM/PM
 * @param {*} UTCinSeconds
 */
export const getHourMinFromUTC = (UTCinSeconds) => {
	const msg = window.finesse.utilities.I18n.getString;
	const pad = (n) => (n < 10) ? '0' + n : n;
	const date = new Date(UTCinSeconds * 1000);
	let hours = date.getHours();
	let minutes = date.getMinutes();
	let period = hours >= 12 ? msg('teammessage.pmDesignator') : msg('teammessage.amDesignator');
	if (hours > 12) {
		hours -= 12;
	}
	return pad(hours) + ':' + pad(minutes) + ' ' + period;
};

/** 
 * Returns
 * 	  true, if `child` is visible in `parent` scroll view
 * 	  false, otherwise
*/
export const isVisible = function (child, parent) {
    const { bottom, height, top } = child.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    return top <= parentRect.top ? parentRect.top - top <= height : bottom - parentRect.bottom <= height;
};

/* if you dont know what is debounce, you are in a wrong place */
export const debounce = (cb, s = 100) => {
  let timerRef = null;

  return (e) => {
    if (timerRef) {
      clearTimeout(timerRef);
    }

    timerRef = setTimeout(() => {
      cb(e);
    }, s);
  };
};